import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fingerprintVoiceIdentity,
  loadProfile,
  personaSeedFromName,
  resolveVoiceIdentity,
} from './zeroTTSProfile';
import type {
  TTSProfile,
  VoiceIdentity,
  ZeroTTSDepth,
} from './zeroTTSProfile';
import { ensureZeroTTSBytes } from './zeroTTSSelector';
import {
  clearPersonaCache,
  manifestCacheBytes,
  readManifest,
} from './zeroTTSManifest';
import { installPersona } from './zeroTTSInstaller';
import { ZeroTTSPlayer } from './zeroTTSPlayer';
import type { PlaybackToken } from './zeroTTSPlayer';
import type { KokoroBlendMode, KokoroGender, VoiceSpec } from './types';
import { getOrCreateSpeechBus } from '../hooks/useAudioPlayer';

export type ZeroTTSState =
  | 'idle'
  | 'loading_profile'
  | 'installing'
  | 'ready'
  | 'unavailable'
  | 'disabled'
  | 'error';

export interface UseZeroTTSOptions {
  personaName: string;
  worldSeedHex: string;
  defaultGender: KokoroGender;
  enabled: boolean;
  /** Stage 26.6: mirrors kokoro_blend_mode. When 'blend' AND pinnedSpec is
   *  present, ZeroTTS renders atoms through the blend engine so the cached
   *  filler voice matches the live reply voice exactly. */
  blendMode?: KokoroBlendMode;
  /** Stage 26.6: active persona's pinned blend spec, if any. */
  pinnedSpec?: VoiceSpec | null;
  /** Stage 40: cache depth — picks which profile JSON the loader fetches
   *  AND folds into the voice fingerprint so depth swaps invalidate the
   *  prior bucket. Defaults to 'classic' for back-compat. */
  depth?: ZeroTTSDepth;
}

export interface UseZeroTTSResult {
  state: ZeroTTSState;
  error: string | null;
  progress: { done: number; total: number } | null;
  atomCount: number;
  cacheBytes: number;
  personaSeed: string;
  voiceId: string | null;
  voiceIdentity: VoiceIdentity | null;
  voiceFingerprint: string | null;
  install: () => Promise<void>;
  reinstall: () => Promise<void>;
  clearCache: () => Promise<void>;
  speak: (
    contextName: string,
    payload?: Record<string, string>,
  ) => Promise<PlaybackToken>;
  stopActive: () => void;
}

function noopToken(): PlaybackToken {
  return {
    stop: () => undefined,
    promise: Promise.resolve(),
    atomText: '',
    durationMs: 0,
  };
}

/** Build a stable dependency key for the pinned spec so the effect only
 *  re-runs when the spec actually changes (not on identity). */
function specKey(spec: VoiceSpec | null | undefined): string {
  if (!spec) return '';
  return [
    spec.voiceA,
    spec.voiceB,
    spec.t.toFixed(6),
    spec.pitch.toFixed(4),
    spec.energy.toFixed(4),
  ].join('|');
}

export function useZeroTTS(opts: UseZeroTTSOptions): UseZeroTTSResult {
  const {
    personaName,
    worldSeedHex,
    defaultGender,
    enabled,
    blendMode = 'single',
    pinnedSpec = null,
    depth = 'classic',
  } = opts;
  const personaSeed = personaSeedFromName(personaName);
  const pinnedSpecKey = specKey(pinnedSpec);

  const [state, setState] = useState<ZeroTTSState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<
    { done: number; total: number } | null
  >(null);
  const [atomCount, setAtomCount] = useState(0);
  const [cacheBytes, setCacheBytes] = useState(0);
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [voiceIdentity, setVoiceIdentity] = useState<VoiceIdentity | null>(
    null,
  );
  const [voiceFingerprint, setVoiceFingerprint] = useState<string | null>(
    null,
  );

  const profileRef = useRef<TTSProfile | null>(null);
  const playerRef = useRef<ZeroTTSPlayer | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      setVoiceId(null);
      setVoiceIdentity(null);
      setVoiceFingerprint(null);
      setAtomCount(0);
      setCacheBytes(0);
      setError(null);
      setProgress(null);

      if (!enabled) {
        setState('disabled');
        return;
      }
      if (typeof caches === 'undefined') {
        setState('unavailable');
        return;
      }
      if (!personaSeed) {
        setState('idle');
        return;
      }

      setState('loading_profile');
      try {
        await ensureZeroTTSBytes();
        const profile = await loadProfile(personaSeed, depth);
        if (cancelled) return;
        if (!profile) {
          setState('unavailable');
          return;
        }
        profileRef.current = profile;

        // Stage 26.6: resolve the voice identity the user currently has
        // pinned (or the single-mode fallback). Fingerprint-scoped bucket
        // means a pin change reads an empty manifest → state 'idle'.
        const identity = await resolveVoiceIdentity({
          profile,
          worldSeedHex,
          defaultGender,
          pinnedSpec,
          blendMode,
        });
        if (cancelled) return;
        const fp = fingerprintVoiceIdentity(identity, depth);
        setVoiceIdentity(identity);
        setVoiceFingerprint(fp);

        const manifest = await readManifest(personaSeed);
        if (cancelled) return;

        // If a manifest exists but its fingerprint differs from the active
        // identity's, treat this persona as "not installed for the current
        // voice" — the user has repinned since last install.
        const manifestMatches =
          manifest && manifest.voice_fingerprint === fp;
        if (!manifestMatches) {
          setState('idle');
          return;
        }
        setAtomCount(Object.keys(manifest!.atoms).length);
        setCacheBytes(manifestCacheBytes(manifest));
        setVoiceId(manifest!.voice_id);

        const sb = getOrCreateSpeechBus();
        const player = new ZeroTTSPlayer(
          profile,
          identity,
          fp,
          sb.ctx,
          sb.speechBus,
        );
        await player.ready();
        if (cancelled) {
          player.dispose();
          return;
        }
        playerRef.current = player;
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    personaSeed,
    worldSeedHex,
    defaultGender,
    enabled,
    blendMode,
    pinnedSpecKey,
    depth,
    // pinnedSpec is captured via pinnedSpecKey; don't re-run on reference.
  ]);

  const install = useCallback(async (): Promise<void> => {
    if (!personaSeed) return;
    if (typeof caches === 'undefined') {
      setError('CACHE_STORAGE_UNAVAILABLE');
      setState('unavailable');
      return;
    }

    if (!profileRef.current) {
      const p = await loadProfile(personaSeed, depth);
      if (!p) {
        setError('PROFILE_MISSING');
        setState('unavailable');
        return;
      }
      profileRef.current = p;
    }
    const profile = profileRef.current;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState('installing');
    setError(null);
    setProgress({ done: 0, total: 1 });

    try {
      const identity = await resolveVoiceIdentity({
        profile,
        worldSeedHex,
        defaultGender,
        pinnedSpec,
        blendMode,
      });
      const fp = fingerprintVoiceIdentity(identity, depth);
      setVoiceIdentity(identity);
      setVoiceFingerprint(fp);

      const manifest = await installPersona(profile, {
        identity,
        depth,
        onProgress: (done, total) => setProgress({ done, total }),
        signal: controller.signal,
      });
      setAtomCount(Object.keys(manifest.atoms).length);
      setCacheBytes(manifestCacheBytes(manifest));
      setVoiceId(manifest.voice_id);

      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      const sb2 = getOrCreateSpeechBus();
      const player = new ZeroTTSPlayer(
        profile,
        identity,
        fp,
        sb2.ctx,
        sb2.speechBus,
      );
      await player.ready();
      playerRef.current = player;
      setState('ready');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setState('idle');
      } else {
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
      }
    } finally {
      setProgress(null);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [personaSeed, worldSeedHex, defaultGender, blendMode, pinnedSpecKey, depth]);

  const reinstall = useCallback(async (): Promise<void> => {
    if (!personaSeed) return;
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
    await clearPersonaCache(personaSeed);
    setAtomCount(0);
    setCacheBytes(0);
    setVoiceId(null);
    await install();
  }, [personaSeed, install]);

  const clearCache = useCallback(async (): Promise<void> => {
    if (!personaSeed) return;
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
    await clearPersonaCache(personaSeed);
    setAtomCount(0);
    setCacheBytes(0);
    setVoiceId(null);
    setState('idle');
  }, [personaSeed]);

  const speak = useCallback(
    async (
      contextName: string,
      payload: Record<string, string> = {},
    ): Promise<PlaybackToken> => {
      const player = playerRef.current;
      if (!player) return noopToken();
      try {
        return await player.speak(contextName, payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[zerotts] speak("${contextName}") failed:`, err);
        return noopToken();
      }
    },
    [],
  );

  const stopActive = useCallback((): void => {
    playerRef.current?.stopActive();
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return {
    state,
    error,
    progress,
    atomCount,
    cacheBytes,
    personaSeed,
    voiceId,
    voiceIdentity,
    voiceFingerprint,
    install,
    reinstall,
    clearCache,
    speak,
    stopActive,
  };
}
